import React from "react";

type InfoSectionProps = {
  id: string;
  heading: string;
  subtitle: string;
  paragraphs: string[];
  accentClassName: string;
  dividerClassName: string;
  gradient: string;
  imageSrc?: string;
  imageAlt?: string;
  reverseDesktop?: boolean;
  useLoader?: boolean;
  loader?: React.ReactNode;
};

export function InfoSection({
  id,
  heading,
  subtitle,
  paragraphs,
  accentClassName,
  dividerClassName,
  gradient,
  imageSrc,
  imageAlt,
  reverseDesktop = false,
  useLoader = false,
  loader,
}: InfoSectionProps) {
  const textPanel = (
    <div
      className={`flex-1 p-6 sm:p-10 flex flex-col justify-center bg-white ${
        reverseDesktop ? "xl:border-r" : "xl:border-l"
      } border-slate-100`}
      style={{ background: gradient }}
    >
      <span className={`inline-block text-5xl font-medium tracking-widest uppercase rounded px-3 py-1 mb-4 w-fit ${accentClassName}`}>
        {heading}
      </span>
      <div className={`w-9 h-0.5 rounded mb-4 ${dividerClassName}`} />
      <h2 className="text-2xl sm:text-3xl font-semibold text-[#0A2540] mb-5 leading-tight">
        {subtitle}
      </h2>
      <div className="flex flex-col gap-4 text-[#64748B] text-sm leading-7">
        {paragraphs.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </div>
  );

  const visualPanel = useLoader ? (
    <div className="w-full xl:w-[38%] flex-shrink-0 flex items-center justify-center bg-slate-50 p-4">
      {loader}
    </div>
  ) : (
    <div className="xl:w-[48%] flex-shrink-0 flex items-center justify-center bg-slate-50 p-4">
      <img
        src={imageSrc}
        alt={imageAlt || heading}
        className="w-full h-full object-cover rounded-xl"
        style={{ minHeight: "180px" }}
      />
    </div>
  );

  return (
    <div id={id} className="w-full mb-8 rounded-2xl overflow-hidden shadow-2xl/30">
      <div className="flex flex-col xl:hidden" style={{ background: gradient }}>
        <div className="p-6 pb-0">
          <span className={`inline-block text-3xl md:text-5xl font-medium tracking-widest uppercase rounded px-3 py-1 mb-3 w-fit ${accentClassName}`}>
            {heading}
          </span>
          <div className={`w-9 h-0.5 rounded mb-1 ${dividerClassName}`} />
        </div>
        {useLoader ? (
          <div className="flex items-center justify-center bg-slate-50 mx-4 my-4 rounded-xl min-h-[200px] p-4">
            {loader}
          </div>
        ) : (
          <div className="mx-4 my-4 rounded-xl overflow-hidden min-h-[200px] bg-slate-50 p-2">
            <img src={imageSrc} alt={imageAlt || heading} className="w-full h-48 object-cover rounded-xl" />
          </div>
        )}
        <div className="p-6 pt-2 flex flex-col gap-4">
          <h2 className="text-base md:text-2xl font-semibold text-[#0A2540] mb-1 leading-tight">
            {subtitle}
          </h2>
          <div className="flex flex-col gap-4 text-[#64748B] text-sm leading-7">
            {paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </div>

      <div className={`hidden xl:flex min-h-[280px] ${reverseDesktop ? "xl:flex-row-reverse" : "xl:flex-row"}`}>
        {visualPanel}
        {textPanel}
      </div>
    </div>
  );
}
